/* The nudge to plan further than today.
 *
 * The failure this exists to catch is the ordinary one, and it is invisible
 * from a day view by construction: today looks fine, tomorrow is empty, and so
 * is every day after it — but you only ever see today, so you never notice
 * until the week has gone.
 *
 * Two rules keep it a nudge rather than nagging:
 *
 *   · **It is earned, not scheduled.** It appears only when the days ahead are
 *     genuinely empty. A banner that shows every morning regardless is one
 *     people learn to look past in a week, and then it cannot warn them about
 *     anything.
 *   · **It is dismissable for the session**, and it says something specific —
 *     how many days are empty — rather than "plan ahead!". A number is a fact
 *     somebody can disagree with; an exhortation is just noise.
 */

import { InfoToggle } from './Section';
import Icon from './Icon';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { WeekSummary } from '@shared/types';
import { get } from '../api';
import MapOut from './MapOut';

const DISMISSED = 'amon_ahead_dismissed';

export default function PlanAhead({ today }: { today: string }) {
  const [mapping, setMapping] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISSED) === '1'; } catch { return false; }
  });

  /* The seven days starting today — not the calendar week, because "the rest
     of this week" on a Sunday is one day and says nothing. */
  const ahead = useQuery({
    queryKey: ['ahead', today],
    queryFn: async () => {
      const r = await get<WeekSummary>(`/api/plan/week?from=${today}`);
      return r.ok ? r : null;
    }
  });

  if (dismissed || !ahead.data) return null;

  /* Today is excluded on purpose: this is about the days you cannot see. */
  const later = ahead.data.days.filter(d => d.date > today);
  const empty = later.filter(d => d.plannedMin === 0).length;

  /* Nothing to say when the week is already mapped out. */
  if (!later.length || empty < later.length) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISSED, '1'); } catch { /* private mode */ }
    setDismissed(true);
  };

  return (
    <div className="notice plan-ahead" role="status">
      <span className="notice-icon"><Icon name="map" size={18} /></span>
      <div>
        <p><strong>Nothing is planned past today.</strong></p>
      </div>
      <div className="inline">
        <InfoToggle about="planning ahead" text={<>The next {empty} days are empty. Amon can place what you already owe across
          them in one go — and, more usefully, tell you now what will not fit, while
          there is still a week in which to do something about it.</>} />
        <button className="btn btn-primary btn-sm" onClick={() => setMapping(true)}>
          Map out the week
        </button>
        <button className="btn btn-ghost btn-icon" onClick={dismiss} aria-label="Not now" title="Not now"><Icon name="close" size={16} /></button>
      </div>

      {mapping && <MapOut from={today} defaultDays={7} onClose={() => setMapping(false)} />}
    </div>
  );
}
